// ----------------------------------------------------------------------
// Contact info — the ONE file for all contact details, phone numbers,
// emails, addresses, geo locations, social links and app download links.
//
// Read from here everywhere:
//   - office cards  (sections/web/contact/ConatctHero)
//   - map pins      (sections/web/contact/ConatctMap via getMapMarkers)
//   - footer column (layouts/simple/footer via CONATCTLIST, SOCIALS, LINKS,
//                    LINK_DOWNLOADS)
//   - mobile drawer (layouts/simple/nav/MenuMobile via CONATCTLIST[0])
//
// Each office carries BOTH shapes: phone/lat/lng for the contact page and
// mobile/location for the footer, so address and pin can never drift apart.
// CONTACT_EMAIL is the single common email shared by every office — change it
// once here and it updates everywhere.
// Add a new office by appending one object to OFFICES. That's it.

export const CONTACT_EMAIL = 'info@asapuae.com';
export const EXTRA_EMAIL = 'asapuae123@gmail.com';

export const OFFICES = [
  {
    id: 'uae',
    flag: 'flag:ae-4x3',
    name: 'United Arab Emirates',
    address: 'School zone - Kalba road, university opp - Muwaileh Commercial - Sharjah',
    phone: '+971 54 349 4801',
    email: CONTACT_EMAIL,
    lat: 25.287952695832207,
    lng: 55.45633456416192,
  },
  {
    id: 'india',
    flag: 'flag:in-4x3',
    name: 'India',
    address: 'Opposite Government Technical School, Kuttipuram, Tirur Road, Kuttippuram, Kerala 679 571',
    phone: '+91 75 5880 8704',
    email: CONTACT_EMAIL,
    lat: 10.846982,
    lng: 76.030389,
  },
];

// Footer + mobile drawer shape (same records, footer field names).
export const CONATCTLIST = OFFICES;

// `tel:` needs a digit-only value, the display value needs the readable one.
export const telHref = (phone) => `tel:+${String(phone).replace(/\D/g, '')}`;

// `mailto:` link for an email address.
export const mailHref = (email) => `mailto:${email}`;

// Shared support email card for the footer "Get in touch" column —
// standardized once here instead of repeating the address per office.
export const SUPPORT_EMAIL_CARD = {
  label: 'General enquiries',
  email: CONTACT_EMAIL,
  emailHref: mailHref(CONTACT_EMAIL),
  blurb: 'For courses, licensing routes and support — we reply within one working day.',
};

// One office by id ('uae' | 'india'), or undefined.
export const getOffice = (id) => OFFICES.find((office) => office.id === id);

// { lat, lng } for an office id — everything a map or geo lookup needs.
export const getOfficeGeo = (id) => {
  const office = getOffice(id);
  return office ? { lat: office.lat, lng: office.lng } : undefined;
};

// Marker list for the map, derived from OFFICES so pins match the cards.
export const getMapMarkers = () =>
  OFFICES.filter(
    (office) => Number.isFinite(office.lat) && Number.isFinite(office.lng)
  ).map((office) => ({
    id: office.id,
    lat: office.lat,
    lng: office.lng,
    label: office.name,
  }));

// ----------------------------------------------------------------------
// Social profiles, sitemap links and app-store badges for the footer.
// Moved here from layouts/simple/fdata.js so this file is the single home
// for every contact-adjacent detail.
// ----------------------------------------------------------------------

export const SOCIALS = [
  { name: 'FaceBook', icon: 'eva:facebook-fill', href: 'https://www.facebook.com/asapuae' },
  {
    name: 'Google',
    icon: 'eva:google-fill',
    href: 'https://www.google.com/search?q=asap+marketing+and+studies+consultancy',
  },
  { name: 'Linkedin', icon: 'eva:linkedin-fill', href: 'https://www.linkedin.com/company/asap-uae' },
  { name: 'Twitter', icon: 'eva:twitter-fill', href: 'https://twitter.com/asapuae' },
  { name: 'Instagram', icon: 'ri:instagram-fill', href: 'https://www.instagram.com/asapconsultancy' },
  {
    name: 'YouTube',
    icon: 'ri:youtube-fill',
    href: 'https://www.youtube.com/@asapstudiesconsultancy3858',
  },
];

export const LINKS = [
  {
    headline: 'Sitemap',
    children: [
      { name: 'Home', href: '/' },
      { name: 'About us', href: '/about-us' },
      { name: 'Courses', href: '/courses' },
      { name: 'Achievers', href: '/achievers' },
      { name: 'Contact us', href: '/contact-us' },
      { name: 'Services', href: '/services' },
      { name: 'Our Team', href: '/team' },
    ],
  },
];

export const LINK_DOWNLOADS = [
  {
    name: 'Android',
    logo: '/assets/images/store/playstore.png',
    href: 'https://play.google.com/store/apps/details?id=com.asapuae.garliksofts',
  },
  { name: 'IOS', logo: '/assets/images/store/appstore.png', href: 'https://apps.apple.com/app/id1668307429' },
];

// YouTube channel home — reused by the success-stories grid so the channel
// URL lives in exactly one place.
export const getSocialHref = (name) => SOCIALS.find((social) => social.name === name)?.href;
export const YOUTUBE_CHANNEL_URL = getSocialHref('YouTube');

// Share-link builders for stories, reviews and the contact widget.
// One place owns every outbound share URL so the channels stay consistent.
export const shareLinksFor = (url, text = '') => {
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(text);
  return [
    {
      name: 'WhatsApp',
      icon: 'ic:baseline-whatsapp',
      href: `https://api.whatsapp.com/send?text=${encodedText ? `${encodedText}%20` : ''}${encodedUrl}`,
    },
    {
      name: 'Facebook',
      icon: 'eva:facebook-fill',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      name: 'X',
      icon: 'prime:twitter',
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}${
        encodedText ? `&text=${encodedText}` : ''
      }`,
    },
    {
      name: 'LinkedIn',
      icon: 'eva:linkedin-fill',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
    {
      name: 'Telegram',
      icon: 'ic:baseline-telegram',
      href: `https://t.me/share/url?url=${encodedUrl}${encodedText ? `&text=${encodedText}` : ''}`,
    },
    {
      name: 'Copy link',
      icon: 'ic:round-content-copy',
      href: url,
      copy: true,
    },
  ];
};

// Write-a-review deep link for the current Google place id.
export const writeReviewHref = (placeId) =>
  `https://search.google.com/local/writereview?placeid=${placeId}`;

// ----------------------------------------------------------------------
// WhatsApp deep link + the home contact widget rows.
// Moved here from sections/web/home/ContactWidget.js so every phone, email
// and chat link in the webui comes from this file.
// ----------------------------------------------------------------------

// One number drives both the dial button and the WhatsApp deep link, so the
// two can never drift apart.
export const WHATSAPP_NUMBER = '971526353211';
export const WHATSAPP_MESSAGE = "Hello ASAP, I'd like to know more about your courses.";

// WhatsApp deep link builder — pass any digits-only or display phone.
export const whatsappHrefFor = (phone, message = WHATSAPP_MESSAGE) =>
  `https://api.whatsapp.com/send?phone=${String(phone).replace(/\D/g, '')}&text=${encodeURIComponent(message)}`;

export const whatsappHref = whatsappHrefFor(WHATSAPP_NUMBER);

// Home "Need our help?" rows.
// NOTE: these sales-line numbers differ from the OFFICES lines on purpose —
// the widget dials the sales desk, the contact page dials the offices.
// They still live here so every phone in the webui comes from one file.
export const SALES_PHONES = [
  { label: '+971 52 635 3211', href: 'tel:+971526353211' },
  { label: '+91 79078 80870', href: 'tel:+917907880870' },
];

export const CONTACT_ROWS = [
  {
    locations: {
      id: 'location',
      icon: 'mdi:address-marker-outline',
      label: 'Our offices',
      details: OFFICES.map((office) => ({
        id: office.id,
        name: office.name,
        flag: office.flag,
        address: office.address,
        phone: office.phone,
        phoneHref: telHref(office.phone),
        whatsappHref: whatsappHrefFor(office.phone),
        mapHref: `https://www.google.com/maps/search/?api=1&query=${office.lat},${office.lng}`,
        geo: { lat: office.lat, lng: office.lng },
      })),
    },
    emails: {
      id: 'email',
      icon: 'ic:round-mail-outline',
      label: 'Email',
      datas: [
        { label: CONTACT_EMAIL, href: mailHref(CONTACT_EMAIL) },
        { label: EXTRA_EMAIL, href: mailHref(EXTRA_EMAIL) },
      ],
    },
  },
];
